import SendIcon from "@mui/icons-material/Send"
import { Button, Stack } from "@mui/material"
import { ReactElement } from "react"
import { useForm, FieldValues } from "react-hook-form"

import ExportMpanInput from "./exportMpanInput"
import usePostExportMpan from "../../../services/middleware/hooks/dnoInterface/protected/usePostExportMpan"
import { AppCard, AppCardContent, AppCardHeader } from "../../surfaces/appCard"

interface IExportMpanCardProps {
    applicationId: string
    applicationMpan: string
    mutate?: () => void
    disabled?: boolean
}

/**
 * Export MPAN card component for DNO portal
 * Allows DNOs to add an Export MPAN to move the application to a terminal state
 * Uses simplified validation - only checks first two digits match application MPAN
 */
const ExportMpanCard = ({
    applicationId,
    applicationMpan,
    mutate,
    disabled = false,
}: IExportMpanCardProps): ReactElement => {
    const defaultValues: FieldValues = {
        mpan: "",
    }

    const { control, handleSubmit, reset, watch } = useForm({
        defaultValues,
    })

    const { isSending, postExportMpan } = usePostExportMpan()

    const mpanValue = watch("mpan")
    const hasMpanValue = !!mpanValue?.trim()

    const onSubmit = async (data: FieldValues): Promise<void> => {
        const trimmedMpan = data.mpan?.trim()
        if (!trimmedMpan) {
            return
        }

        await postExportMpan(applicationId, trimmedMpan, () => {
            reset()
            if (mutate) {
                mutate()
            }
        })
    }

    const isSubmitDisabled = !hasMpanValue || isSending || disabled

    return (
        <AppCard>
            <AppCardHeader
                avatar={<SendIcon fontSize="large" />}
                title="Export MPAN"
                subheader="This application is available to have an Export MPAN added to it. This will move this application into a new terminal state."
                titleTypographyProps={{
                    variant: "h5",
                    fontWeight: "500",
                }}
            />
            <AppCardContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack spacing={2}>
                        <ExportMpanInput
                            registerName="mpan"
                            control={control}
                            applicationMpan={applicationMpan}
                            disabled={disabled || isSending}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            endIcon={<SendIcon />}
                            disabled={isSubmitDisabled}
                            sx={{ alignSelf: "flex-end" }}
                        >
                            {isSending ? "Submitting..." : "Submit"}
                        </Button>
                    </Stack>
                </form>
            </AppCardContent>
        </AppCard>
    )
}

ExportMpanCard.defaultProps = {
    mutate: undefined,
    disabled: false,
}

export default ExportMpanCard
